// One controlled document (rule 37): what is in force, the version being worked on, and the acts on each. Every act is
// the server's to refuse; the screen shows what a grant allows and says the server's own sentence when it refuses.
// Claude's reading is labelled a suggestion, and nothing it says changes a status.
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { api } from '../../lib/api';
import { usePageTitle } from '../../lib/pageTitle';
import { fieldClass, labelClass, officeFocus, serverMessage, useMyGrants, useResource } from '../../shells/OfficeShell';
import { FileViewer } from './CertificateFile';
import { TONE_CLASS, chipClass, stamp } from './goodsInModel';
import { ALIAS_LABEL, GATE_LABEL, STATUS_LABEL, STATUS_TONE, attentionOf, brisbaneToday, type DocumentDetail, type VersionView } from './documentsModel';

const muted = 'm-0 text-(length:--fs-sm) text-(--muted)';
const card = 'flex flex-col gap-(--s3) rounded-(--radius) border border-(--border) bg-(--card) p-(--s4)';
const h2 = 'm-0 text-(length:--fs-md) font-semibold';

function ViewFile({ documentId, v, label, onOpened }: { documentId: number; v: VersionView; label: string; onOpened?: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Sheet
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) onOpened?.();
      }}
    >
      <SheetTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          {label}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full flex-col gap-(--s2) sm:max-w-[min(1000px,92vw)]">
        <SheetHeader>
          <SheetTitle>{v.issuedName ?? `Version ${v.versionNo}`}</SheetTitle>
          <SheetDescription>Version {v.versionNo}, the copy kept at approval</SheetDescription>
        </SheetHeader>
        {open ? <FileViewer path={`/documents/${documentId}/file?version=${v.id}`} label={v.issuedName ?? `version-${v.versionNo}.pdf`} title={`Version ${v.versionNo}`} /> : null}
      </SheetContent>
    </Sheet>
  );
}

/** One act: a note (and maybe a date) typed in place, sent once, the server's sentence on refusal. */
function ActPanel({ label, noteLabel, noteRequired, withDate, tone, onSend, onCancel }: { label: string; noteLabel: string; noteRequired: boolean; withDate?: boolean; tone?: 'default' | 'outline'; onSend: (b: { note: string; effectiveFrom?: string }) => Promise<void>; onCancel: () => void }) {
  const [note, setNote] = useState('');
  const [date, setDate] = useState(brisbaneToday());
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  async function send() {
    setBusy(true);
    setFailed(null);
    try {
      await onSend({ note, ...(withDate ? { effectiveFrom: date } : {}) });
    } catch (err) {
      setFailed(serverMessage(err));
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-col gap-(--s2) rounded-(--radius-sm) border border-(--border) p-(--s3)">
      {withDate ? (
        <label className="flex w-fit flex-col gap-(--s1)">
          <span className={labelClass}>Takes effect</span>
          <input type="date" className={`${fieldClass} ${officeFocus}`} min={brisbaneToday()} value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
      ) : null}
      <label className="flex flex-col gap-(--s1)">
        <span className={labelClass}>{noteLabel}</span>
        <textarea className={`${fieldClass} min-h-[88px] py-(--s2) ${officeFocus}`} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {failed ? (
        <p role="alert" className="m-0 font-semibold text-(--bad)">
          {failed}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-(--s2)">
        <Button type="button" variant={tone ?? 'default'} disabled={busy || (noteRequired && !note.trim())} onClick={() => void send()}>
          {busy ? 'Working…' : label}
        </Button>
        <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function Suggestions({ d, v }: { d: DocumentDetail; v: VersionView }) {
  const own = d.suggestions.filter((s) => s.versionId === v.id);
  const diff = own.find((s) => s.kind === 'DIFF');
  const gate = own.find((s) => s.kind === 'GATE_DRIFT');
  if (!diff && !gate) return null;
  return (
    <div className="flex flex-col gap-(--s2) rounded-(--radius-sm) border border-dashed border-(--border-strong) p-(--s3)">
      <p className={`${labelClass} m-0`}>Claude's reading: a suggestion, not a judgement</p>
      {diff?.body?.summary ? <p className="m-0 text-(length:--fs-sm)">{diff.body.summary}</p> : null}
      {diff?.body?.changes?.length ? (
        <ul className="m-0 list-disc pl-(--s4) text-(length:--fs-sm)">
          {diff.body.changes.map((c, i) => (
            <li key={i}>
              <strong>{c.section}:</strong> {c.change}
            </li>
          ))}
        </ul>
      ) : null}
      {gate?.body?.disagreements?.length ? (
        <>
          <p className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">Where it may disagree with what the app enforces</p>
          <ul className="m-0 list-disc pl-(--s4) text-(length:--fs-sm)">
            {gate.body.disagreements.map((g, i) => (
              <li key={i}>
                <strong>{GATE_LABEL[g.gate] ?? g.gate}:</strong> {g.disagreement} <span className="text-(--muted)">The procedure says: "{g.procedureSays}"</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

export function DocumentPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const res = useResource<DocumentDetail>(documentId ? `/documents/${documentId}` : null);
  const grants = useMyGrants();
  const d = res.data;
  usePageTitle(d?.docId ?? null);
  const [act, setAct] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [alias, setAlias] = useState({ kind: 'LEGACY_CODE', alias: '' });
  const [busy, setBusy] = useState(false);

  if (res.error) {
    return (
      <div role="alert" className="flex flex-col gap-(--s2)">
        <p className="m-0 font-semibold text-(--warn)">Could not read this document: {res.error}</p>
        <div>
          <Button variant="outline" type="button" onClick={res.reload}>
            Try again
          </Button>
        </div>
      </div>
    );
  }
  if (!d) return <p role="status" className={muted}>Loading…</p>;

  const mayAuthor = grants.known && grants.holds('DOC_CONTROL');
  const mayApprove = grants.known && grants.holds('DOC_APPROVE');
  const effective = d.versions.find((v) => v.status === 'EFFECTIVE') ?? null;
  const open = d.versions.find((v) => ['IMPORTED', 'DRAFT', 'IN_REVIEW'].includes(v.status)) ?? null;
  const approved = d.versions.find((v) => v.status === 'APPROVED') ?? null;
  const base = `/documents/${d.id}`;

  async function post(path: string, body: Record<string, unknown>, done?: string) {
    await api.post(path, body);
    setAct(null);
    setMessage(done ?? null);
    res.reload();
  }
  async function simple(path: string, body: Record<string, unknown>, done?: string) {
    setBusy(true);
    setFailed(null);
    try {
      await post(path, body, done);
    } catch (err) {
      setFailed(serverMessage(err));
    } finally {
      setBusy(false);
    }
  }
  const attention = attentionOf(d);

  return (
    <div className="flex flex-col gap-(--s4)">
      <header className="flex flex-col gap-(--s2)">
        <Link to="/office/documents" className={`text-(length:--fs-sm) underline ${officeFocus}`}>
          Documents
        </Link>
        <h1 className="m-0 font-display text-[30px] font-semibold leading-tight tracking-[-0.01em]">
          <span className="font-mono">{d.docId}</span> {d.title}
        </h1>
        <div className="flex flex-wrap items-center gap-(--s2)">
          <span className={`${chipClass} w-fit ${TONE_CLASS[d.retired ? 'quiet' : STATUS_TONE[d.status]]}`}>
            {d.retired ? 'Retired' : STATUS_LABEL[d.status]}
            {d.versionNo ? ` · v${d.versionNo}` : ''}
          </span>
          {attention.map((a) => (
            <span key={a} className={`${chipClass} w-fit ${TONE_CLASS.warn}`}>
              {a}
            </span>
          ))}
        </div>
        <p className={muted}>
          Owner {d.ownerRole} · Approver {d.approverRole}
          {d.reviewerRoles.length ? ` · Reviewers ${d.reviewerRoles.join(', ')}` : ''}
          {d.assetId ? ` · D365 asset ${d.assetId}` : ''}
        </p>
        {d.retired ? <p className={muted}>Retired {stamp(d.retiredAt)} by {d.retiredByUpn}: {d.retiredReason}</p> : null}
        {d.master?.webUrl ? (
          <div>
            <a href={d.master.webUrl} target="_blank" rel="noopener noreferrer" className={`text-(length:--fs-sm) underline ${officeFocus}`}>
              Open the master in Word
            </a>
            <span className="ml-(--s2) text-(length:--fs-sm) text-(--muted)">last edited {stamp(d.master.modifiedAt)}{d.master.modifiedByUpn ? ` by ${d.master.modifiedByUpn}` : ''}</span>
          </div>
        ) : d.standing.masterMissing === null ? (
          <p className={muted}>The library has not been read yet, so the app does not know where the master is.</p>
        ) : null}
      </header>

      {message ? <p role="status" className="m-0 font-semibold">{message}</p> : null}
      {failed ? <p role="alert" className="m-0 font-semibold text-(--bad)">{failed}</p> : null}

      <section className={card} aria-label="In force">
        <h2 className={h2}>In force</h2>
        {effective ? (
          <>
            <p className="m-0 text-(length:--fs-sm)">
              Version {effective.versionNo}, from {effective.effectiveFrom}. Review by {effective.reviewBy ?? 'not set'}. Approved by {effective.approvedByUpn} {stamp(effective.approvedAt)}.
              {!effective.issuedInLibrary ? ' Not yet in the Issued library: the kept copy stands, and the app tries again.' : ''}
            </p>
            <div className="flex flex-wrap gap-(--s2)">
              <ViewFile documentId={d.id} v={effective} label="Read the PDF" onOpened={() => setOpened(true)} />
              {d.mine?.applies && !d.mine.acknowledgedAt ? (
                <Button type="button" disabled={busy || !opened} title={opened ? undefined : 'Open the PDF first'} onClick={() => void simple(`${base}/versions/${effective.id}/acknowledge`, { via: 'OFFICE' }, 'Acknowledged.')}>
                  I have read this
                </Button>
              ) : null}
              {d.mine?.acknowledgedAt ? <span className={muted}>You acknowledged this {stamp(d.mine.acknowledgedAt)}.</span> : null}
              {mayApprove && act !== 'withdraw' ? (
                <Button type="button" variant="outline" size="sm" onClick={() => setAct('withdraw')}>
                  Withdraw
                </Button>
              ) : null}
            </div>
            {act === 'withdraw' ? (
              <ActPanel label="Withdraw this version" noteLabel="Why is it being withdrawn? Nothing takes its place by itself." noteRequired onCancel={() => setAct(null)} onSend={(b) => post(`${base}/versions/${effective.id}/withdraw`, b, 'Withdrawn.')} />
            ) : null}
            {d.ack ? <p className={muted}>{d.ack.acknowledged} of {d.ack.applies} people have read it.</p> : <p className={muted}>It applies to nobody yet.</p>}
          </>
        ) : (
          <p className={muted}>No version is in force.</p>
        )}
        {approved ? (
          <p className="m-0 text-(length:--fs-sm)">
            Version {approved.versionNo} is approved and takes effect {approved.effectiveFrom}.
          </p>
        ) : null}
      </section>

      <section className={card} aria-label="Being worked on">
        <h2 className={h2}>Being worked on</h2>
        {open ? (
          <>
            <p className="m-0 text-(length:--fs-sm)">
              <span className={`${chipClass} mr-(--s2) w-fit ${TONE_CLASS[STATUS_TONE[open.status]]}`}>{STATUS_LABEL[open.status]}</span>
              Version {open.versionNo}, opened by {open.draftedByUpn}
              {open.draftVia === 'GATEWAY' ? ' through chat' : ''} {stamp(open.draftedAt)}.
              {open.legacyVersion ? ` Old version ${open.legacyVersion}.` : ''}
            </p>
            {open.changeNote ? <p className="m-0 text-(length:--fs-sm)">What is changing: {open.changeNote}</p> : null}
            {open.returnNote ? (
              <p className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
                Returned by {open.returnedByUpn}: {open.returnNote}
              </p>
            ) : null}
            {open.proposedText ? (
              <details className="text-(length:--fs-sm)">
                <summary className="cursor-pointer font-semibold">Proposed wording from chat: write what you accept into the master</summary>
                <pre className="mt-(--s2) max-h-[320px] overflow-auto whitespace-pre-wrap rounded-(--radius-sm) bg-(--subtle) p-(--s2) font-sans">{open.proposedText}</pre>
              </details>
            ) : null}
            {open.status === 'IN_REVIEW' ? (
              <p className={muted}>
                Submitted by {open.submittedByUpn} {stamp(open.submittedAt)}: {open.masterName}
                {open.masterModifiedByUpn ? `, last edited by ${open.masterModifiedByUpn}` : ''}.
              </p>
            ) : null}
            {open.warnings.length ? (
              <ul className="m-0 list-disc pl-(--s4) text-(length:--fs-sm) text-(--warn)">
                {open.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            ) : null}
            <Suggestions d={d} v={open} />
            <div className="flex flex-wrap gap-(--s2)">
              {(open.status === 'DRAFT' || open.status === 'IMPORTED') && mayAuthor ? (
                <Button type="button" disabled={busy} onClick={() => void simple(`${base}/versions/${open.id}/submit`, {}, 'Submitted for approval.')}>
                  {busy ? 'Reading the master…' : 'Submit for approval'}
                </Button>
              ) : null}
              {open.status === 'IN_REVIEW' && mayApprove && act === null ? (
                <>
                  <Button type="button" onClick={() => setAct('approve')}>
                    Approve
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setAct('return')}>
                    Return
                  </Button>
                </>
              ) : null}
            </div>
            {act === 'approve' ? <ActPanel label="Approve this version" noteLabel="Anything to record (optional)" noteRequired={false} withDate onCancel={() => setAct(null)} onSend={(b) => post(`${base}/versions/${open.id}/approve`, b, 'Approved. The kept copy is the record.')} /> : null}
            {act === 'return' ? <ActPanel label="Return it" noteLabel="What needs to change?" noteRequired tone="outline" onCancel={() => setAct(null)} onSend={(b) => post(`${base}/versions/${open.id}/return`, b, 'Returned to the author.')} /> : null}
          </>
        ) : !d.retired && mayAuthor ? (
          act === 'open' ? (
            <ActPanel label="Open a new version" noteLabel="What is changing, and why?" noteRequired onCancel={() => setAct(null)} onSend={(b) => post(`${base}/versions`, { changeNote: b.note }, 'New version opened. Edit the master in Word, then submit it.')} />
          ) : (
            <div>
              <Button type="button" variant="outline" onClick={() => setAct('open')}>
                Open a new version
              </Button>
            </div>
          )
        ) : (
          <p className={muted}>Nothing is being worked on.</p>
        )}
      </section>

      {d.people ? (
        <section className={card} aria-label="Who has read it">
          <h2 className={h2}>Who has read the version in force</h2>
          {d.people.length ? (
            <ul className="m-0 grid list-none gap-(--s1) p-0 text-(length:--fs-sm) sm:grid-cols-2">
              {d.people.map((p) => (
                <li key={p.upn} className={p.acknowledgedAt ? '' : 'font-semibold'}>
                  {p.upn}: {p.acknowledgedAt ? stamp(p.acknowledgedAt) : 'not yet'}
                </li>
              ))}
            </ul>
          ) : (
            <p className={muted}>It applies to nobody yet.</p>
          )}
        </section>
      ) : null}

      {d.governs.length ? (
        <section className={card} aria-label="What it governs">
          <h2 className={h2}>What it governs in the app</h2>
          <ul className="m-0 list-disc pl-(--s4) text-(length:--fs-sm)">
            {d.governs.map((g) => (
              <li key={g}>{GATE_LABEL[g] ?? g}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className={card} aria-label="Versions">
        <h2 className={h2}>Versions</h2>
        <ul className="m-0 flex list-none flex-col gap-(--s2) p-0 text-(length:--fs-sm)">
          {d.versions.map((v) => (
            <li key={v.id} className="flex flex-wrap items-center gap-(--s2)">
              <span className="font-semibold">v{v.versionNo}</span>
              <span className={`${chipClass} w-fit ${TONE_CLASS[STATUS_TONE[v.status]]}`}>{STATUS_LABEL[v.status]}</span>
              <span className="text-(--muted)">
                {v.effectiveFrom ? `${v.effectiveFrom}${v.effectiveTo ? ` to ${v.effectiveTo}` : ''}` : ''} {v.approvedByUpn ? `approved by ${v.approvedByUpn}` : ''} {v.withdrawNote ? `withdrawn: ${v.withdrawNote}` : ''}
              </span>
              {v.kept ? <ViewFile documentId={d.id} v={v} label="PDF" /> : null}
            </li>
          ))}
        </ul>
      </section>

      <section className={card} aria-label="Old references">
        <h2 className={h2}>Old references</h2>
        {d.aliases.length ? (
          <ul className="m-0 list-none p-0 text-(length:--fs-sm)">
            {d.aliases.map((a) => (
              <li key={a.id}>
                {ALIAS_LABEL[a.kind] ?? a.kind}: <span className="font-mono">{a.alias}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={muted}>None.</p>
        )}
        {mayAuthor || mayApprove ? (
          <div className="flex flex-wrap items-end gap-(--s2)">
            <label className="flex flex-col gap-(--s1)">
              <span className={labelClass}>Kind</span>
              <select className={`${fieldClass} ${officeFocus}`} value={alias.kind} onChange={(e) => setAlias({ ...alias, kind: e.target.value })}>
                {Object.entries(ALIAS_LABEL).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex min-w-[260px] flex-1 flex-col gap-(--s1)">
              <span className={labelClass}>What it says</span>
              <Input value={alias.alias} onChange={(e) => setAlias({ ...alias, alias: e.target.value })} />
            </label>
            <Button
              type="button"
              variant="outline"
              disabled={busy || !alias.alias.trim()}
              onClick={() => {
                void simple(`${base}/aliases`, alias);
                setAlias({ kind: alias.kind, alias: '' });
              }}
            >
              Add
            </Button>
          </div>
        ) : null}
      </section>

      {mayApprove && !d.retired ? (
        act === 'retire' ? (
          <ActPanel label="Retire this document" noteLabel="Why? Its number is never reused." noteRequired tone="outline" onCancel={() => setAct(null)} onSend={(b) => post(`${base}/retire`, { reason: b.note }, 'Retired.')} />
        ) : (
          <div>
            <Button type="button" variant="outline" size="sm" onClick={() => setAct('retire')}>
              Retire this document
            </Button>
          </div>
        )
      ) : null}
    </div>
  );
}

export default DocumentPage;
